/** Calendar inputs are IST wall time, independent of device timezone. */
export function istInput(value = new Date().toISOString()): string {
  return new Date(new Date(value).getTime() + 330 * 60000).toISOString().slice(0, 16);
}
export function istISO(value: string): string { return new Date(`${value}:00+05:30`).toISOString(); }
export function istDay(value = new Date().toISOString()): string { return istInput(value).slice(0, 10); }
export function dayOffset(days: number, from = istDay()): string {
  return new Date(new Date(`${from}T12:00:00Z`).getTime() + days * 86400000).toISOString().slice(0, 10);
}
export function nextMonday(from = istDay()): string {
  const day = new Date(`${from}T12:00:00Z`).getUTCDay();
  return dayOffset((8 - day) % 7 || 7, from);
}
export function defaultAttendee(owner: number | null | undefined, current: number | undefined, coordinator: boolean, eligible: number[]): number | null {
  if (owner && eligible.includes(owner)) return owner;
  return !coordinator && current && eligible.includes(current) ? current : null;
}
/** Logging what already happened: the person recording it did it, unless they cannot be an attendee here. */
export function defaultLogAttendee(current: number | undefined, eligible: number[]): number | null {
  return current && eligible.includes(current) ? current : null;
}
export function editableTouchpoint(row: { type: string }): boolean { return row.type !== 'email'; }
export function touchpointStatusLabel(status: string): string {
  return ({scheduled:'Planned',completed:'Done',cancelled:'Cancelled',no_show:'No-show'} as Record<string,string>)[status] ?? 'Record';
}
/** Non-completed timeline events use their creation clock, not a guessed outcome time. */
export function timelineClockLabel(row: {status:string;type:string}): string {
  return row.status==='completed' ? (row.type==='task'?'Task completed':'Happened') : 'Added';
}
export function contactCompanyInfo(person: {company_id:number|null;company?:{id:number;name:string;city?:string;address?:Record<string,unknown>|null}|null}, fallback?:{id:number;name:string;city?:string;address?:Record<string,unknown>|null}|null) {
  return [person.company,fallback].find(company=>company && company.id===person.company_id) ?? undefined;
}
export function touchpointClock(row: { occurred_at?: string | null; type: string; scheduled_at?: string | null; created_at: string; email?: { sent_at: string | null } }): string {
  return row.occurred_at || (row.type==='email' ? row.email?.sent_at : null) || (['meeting','call','visit'].includes(row.type) ? row.scheduled_at : null) || row.created_at;
}
export const outcomeTags = ['interested', 'quote_requested', 'price_objection', 'not_reachable', 'not_interested', 'won', 'lost'];
export function csvCell(value: unknown): string {
  let text = String(value ?? '');
  if (/^[\s]*[=+@-]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}
export const detailFields = ['first_name', 'last_name', 'email', 'phone', 'whatsapp', 'job_title', 'city', 'relationship_type', 'notes', 'company_id', 'hotel_company_id', 'brand_company_id', 'owner_company_id', 'custom'] as const;
export function contactDetailsBody(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(detailFields.filter(key => key in value).map(key => [key, key.endsWith('company_id') ? value[key] ?? null : value[key]]));
}
export function draftKey(userId: number, personId: number, activityId?: number): string { return `touchpoint-draft:${userId}:${personId}:${activityId ?? 'new'}`; }
export function agendaBucket(value: string, now = new Date().toISOString()): string {
  const date = value.length === 10 ? value : istDay(value), today = istDay(now);
  if (date < today) return 'overdue';
  if (date === today) return 'today';
  if (date === dayOffset(1, today)) return 'tomorrow';
  return date < nextMonday(today) ? 'week' : 'later';
}

// ---- Display helpers shared by the contacts & touchpoints screens ----------

const typeLabels: Record<string, string> = {
  call: 'Call', cold_call: 'Cold call', meeting: 'Meeting', visit: 'Site visit',
  whatsapp: 'WhatsApp', note: 'Note', task: 'Task', email: 'Email'
};
/** Human label for an activity type; unknown types are sentence-cased, never raw snake_case. */
export function touchpointTypeLabel(type: string | null | undefined): string {
  if (!type) return 'Touchpoint';
  return typeLabels[type] ?? sentenceCase(type);
}
const outcomeLabels: Record<string, string> = {
  interested: 'Interested', quote_requested: 'Quote requested', price_objection: 'Price objection',
  not_reachable: 'Not reachable', not_interested: 'Not interested', won: 'Won', lost: 'Lost'
};
export function outcomeLabel(tag: string): string {
  return outcomeLabels[tag] ?? sentenceCase(tag);
}
export function sentenceCase(value: string): string {
  const text = value.replaceAll('_', ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

function dayNumber(day: string): number {
  return Math.round(Date.parse(`${day}T12:00:00Z`) / 86400000);
}
/** Relative IST day: "Today", "Yesterday", "In 3 days", "9 days ago", "3 months ago". */
export function relativeDay(value: string | null | undefined, now = new Date().toISOString()): string {
  if (!value) return '';
  const day = value.length === 10 ? value : istDay(value);
  const diff = dayNumber(day) - dayNumber(istDay(now));
  if (!Number.isFinite(diff)) return '';
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const n = Math.abs(diff);
  const amount = n < 14 ? plural(n, 'day') : n < 60 ? plural(Math.round(n / 7), 'week') : n < 730 ? plural(Math.round(n / 30), 'month') : plural(Math.round(n / 365), 'year');
  return diff > 0 ? `In ${amount}` : `${amount} ago`;
}
/** "Mon, 2 Nov" (adds the year when it is not the current IST year). */
export function shortDay(day: string, now = new Date().toISOString()): string {
  const date = new Date(`${day.slice(0, 10)}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return day;
  const sameYear = day.slice(0, 4) === istDay(now).slice(0, 4);
  return new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) }).format(date);
}
/** Group heading for a day: "Today · Mon, 2 Nov", "Yesterday · …", otherwise just the date. */
export function dayHeading(day: string, now = new Date().toISOString()): string {
  const relative = relativeDay(day, now);
  return ['Today', 'Yesterday', 'Tomorrow'].includes(relative) ? `${relative} · ${shortDay(day, now)}` : shortDay(day, now);
}
/** IST clock time, e.g. "10:30". */
export function istTime(value: string): string {
  return istInput(value).slice(11, 16);
}

export interface NextStepActivity {
  id: number; status: string; type: string; title: string;
  next_action?: string; next_action_date?: string | null;
  scheduled_at?: string | null; due_at?: string | null;
  assignee_user_id?: number | null; assignee_name?: string | null;
}
export interface ContactNextStep<T extends NextStepActivity = NextStepActivity> {
  kind: 'plan' | 'reminder'; activity: T; title: string; day: string | null; time: string | null; overdue: boolean;
}
/**
 * The contact's real next step: whichever comes first of the latest
 * conversation's reminder and the earliest open plan (overdue plans included).
 * A plan wins a tie because it is concrete, booked work.
 */
export function contactNextStep<T extends NextStepActivity>(reminder: T | null | undefined, plan: T | null | undefined, now = new Date().toISOString()): ContactNextStep<T> | null {
  const today = istDay(now);
  const fromPlan = (a: T): ContactNextStep<T> => {
    const when = a.scheduled_at ?? a.due_at ?? null;
    const day = when ? istDay(when) : null;
    return { kind: 'plan', activity: a, title: a.title || touchpointTypeLabel(a.type), day, time: a.scheduled_at ? istTime(a.scheduled_at) : null, overdue: !!day && day < today };
  };
  const fromReminder = (a: T): ContactNextStep<T> => {
    const day = a.next_action_date?.slice(0, 10) ?? null;
    return { kind: 'reminder', activity: a, title: a.next_action || a.title, day, time: null, overdue: !!day && day < today };
  };
  const p = plan ? fromPlan(plan) : null;
  const r = reminder ? fromReminder(reminder) : null;
  if (!p || !r) return p ?? r;
  if (!p.day) return r.day ? r : p;
  if (!r.day) return p;
  return p.day <= r.day ? p : r;
}
/** Activity date field to send when moving a plan or reminder to `day` (keeps an appointment's IST clock time). */
export function moveTouchpointBody(row: { status: string; scheduled_at?: string | null; due_at?: string | null }, day: string): Record<string, string> {
  if (row.status !== 'scheduled') return { next_action_date: `${day}T00:00:00Z` };
  const value = istISO(`${day}T${istInput(row.scheduled_at ?? row.due_at ?? new Date().toISOString()).slice(11)}`);
  return row.scheduled_at ? { scheduled_at: value } : { due_at: value };
}

/** The next day calls are made: tomorrow, or Monday when tomorrow is Sunday. */
export function nextWorkingDay(day: string): string {
  const next = dayOffset(1, day);
  return new Date(`${next}T12:00:00Z`).getUTCDay() === 0 ? dayOffset(1, next) : next;
}
